import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { createProductQuote } from '@app/api/action/Order';
import { RootReducer, Store } from '@app/app.reducers';
import {
	getQuoteFormSubmitError,
	getQuoteFormSubmitProcessing,
	getQuoteFormSubmitSuccess,
	resetQuoteFormStatus,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { getId as getStorefrontId } from '@app/shared/reducers/storefront.reducer';
import { popErrorToast } from '@app/shared/reducers/toast.reducer';
import { FormErrors, formValidationErrors$, ValidationMessages } from '@app/shared/utils/form-helper';
import { I18nService } from '@app/shared/utils/i18n.service';
import { filterTruthy } from '@app/shared/utils/util';
import type { FileItem } from '@app/upload/upload.component';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/** Dialog for requesting a quote for a product. */
@UntilDestroy()
@Component({
	selector: 'g-product-form-quote-dialog',
	templateUrl: './product-form-quote-dialog.component.html',
	styleUrls: ['./product-form-quote-dialog.component.scss'],
})
export class ProductFormQuoteDialogComponent implements OnInit {
	static readonly MAX_WIDTH = '880px';
	readonly icons = { faXmark, faSpinner };
	readonly dialogData?: { product: api.ProductDto };
	productFormQuoteForm: UntypedFormGroup;
	formErrors$: FormErrors;
	validationMessages: ValidationMessages;
	product: api.ProductDto;
	isProcessing: boolean;
	isSuccess: boolean;
	formTitle: string;
	formDescription: SafeHtml;
	isCompanyVisible: boolean;
	private storeId: number;
	private attachment: string;

	constructor(
		protected readonly store: Store<RootReducer.State>,
		private readonly fb: UntypedFormBuilder,
		private readonly sanitizer: DomSanitizer,
		private readonly modal: NgbActiveModal,
		private readonly i18n: I18nService,
	) {
		this.validationMessages = {
			firstName: {
				required: $localize`:@@ContactInfoFirstNameRequired:First name is required`,
				maxlength: $localize`:@@ContactInfoFirstNameMaxLength:First name cannot be over 255 characters long`,
			},
			lastName: {
				required: $localize`:@@ContactInfoLastNameRequired:Last name is required`,
				maxlength: $localize`:@@ContactInfoLastNameMaxLength:Last name cannot be over 255 characters long`,
			},
			email: {
				required: $localize`:@@ContactInfoEmailRequired:Email is required`,
				maxlength: $localize`:@@ContactInfoEmailMaxLength:Email cannot be over 255 characters long`,
				email: $localize`:@@ContactInfoEmailFormat:Email has to be in right format`,
			},
			phone: {
				required: $localize`:@@ContactInfoPhoneRequired:Phone number is required`,
				pattern: $localize`:@@ContactInfoPhoneFormat:Phone number has to be in valid format`,
			},
			company: {
				maxlength: $localize`:@@ContactInfoCompanyMaxLength:Company name cannot be over 255 characters long`,
			},
			message: {
				required: $localize`:@@ContactRequestMessageRequired:Message is required`,
			},
		};
	}

	ngOnInit(): void {
		this.buildForm();
		this.store
			.select(getStorefrontId)
			.pipe(untilDestroyed(this))
			.subscribe((storefrontId) => (this.storeId = storefrontId));
		this.product = this.dialogData?.product;
		this.formTitle = this.i18n.getText(this.product?.metadata?.form_title);
		this.formDescription = this.sanitizer.bypassSecurityTrustHtml(
			this.i18n.getText(this.product?.metadata?.form_description),
		);
		this.isCompanyVisible = this.product?.metadata?.ask_company_name === true;
		this.store
			.select(getQuoteFormSubmitProcessing)
			.pipe(untilDestroyed(this))
			.subscribe((isProcessing) => (this.isProcessing = isProcessing === true));
		this.store
			.select(getQuoteFormSubmitError)
			.pipe(filterTruthy, untilDestroyed(this))
			.subscribe(() =>
				this.store.dispatch(
					popErrorToast({
						title: $localize`:@@ToastProductFormError:Failed to submit form. Try again later.`,
					}),
				),
			);
		this.store
			.select(getQuoteFormSubmitSuccess)
			.pipe(filterTruthy, untilDestroyed(this))
			.subscribe((isSuccess) => (this.isSuccess = isSuccess));
	}

	setAttachment({ fileUrl }: FileItem): void {
		this.attachment = fileUrl;
	}

	onSubmit(): void {
		this.store.dispatch(
			createProductQuote({
				body: {
					first_name: this.productFormQuoteForm.value.firstName,
					last_name: this.productFormQuoteForm.value.lastName,
					email: this.productFormQuoteForm.value.email || null,
					phone:
						typeof this.productFormQuoteForm.value.phone === 'string'
							? this.productFormQuoteForm.value.phone.replaceAll(' ', '')
							: null,
					message: this.productFormQuoteForm.value.message,
					company: this.productFormQuoteForm.value.company || null,
					product_id: this.product.id!,
					attachment: this.attachment,
					store_id: this.storeId, // TODO: Remove.
				},
			}),
		);
	}

	closeDialog() {
		// Reset form status when dialog is closed.
		this.store.dispatch(resetQuoteFormStatus());
		this.modal.close();
	}

	private buildForm() {
		this.productFormQuoteForm = this.fb.group({
			firstName: ['', [Validators.required, Validators.maxLength(255)]],
			lastName: ['', [Validators.required, Validators.maxLength(255)]],
			email: ['', [Validators.maxLength(255), Validators.email, Validators.required]],
			phone: ['', [Validators.pattern(/^(\+?)([0-9 ]+)$/), Validators.required]],
			message: ['', [Validators.required]],
			company: ['', [Validators.maxLength(255)]],
		});

		this.formErrors$ = formValidationErrors$(this.productFormQuoteForm, this.validationMessages);
	}
}
